import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Tag, CheckCircle } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';

const LS_DISMISSED = 'rumio_email_popup_dismissed';
const LS_SUBSCRIBED = 'rumio_email_popup_subscribed';

interface EmailPopupConfig {
  enabled: boolean;
  headline: string;
  body: string;
  button_text: string;
  success_message: string;
  delay_seconds: number;
  show_incentive: boolean;
  incentive_code: string;
  incentive_description: string;
}

export default function EmailSignupPopup() {
  const { data } = useSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const config = data?.settings?.email_popup as EmailPopupConfig | undefined;

  useEffect(() => {
    if (!config?.enabled) return;
    if (localStorage.getItem(LS_DISMISSED) || localStorage.getItem(LS_SUBSCRIBED)) return;

    timerRef.current = setTimeout(() => {
      setIsOpen(true);
    }, (config.delay_seconds || 0) * 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [config]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      clearTimeout(focusTimer);
    };
  }, [isOpen]);

  function handleDismiss() {
    setIsOpen(false);
    if (!isSuccess) {
      localStorage.setItem(LS_DISMISSED, '1');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setError('');
    setIsSubmitting(true);

    try {
      await api.post('/newsletter/subscribe', { email: email.trim() });
      localStorage.setItem(LS_SUBSCRIBED, '1');
      setIsSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!config?.enabled) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Email signup"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleDismiss}
            aria-hidden="true"
          />

          {/* Content */}
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl bg-surface shadow-xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute right-3 top-3 z-10 rounded-lg p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
              aria-label="Close popup"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>

            <div className="p-6 pt-8 text-center">
              {!isSuccess ? (
                <>
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sage-100">
                    <Mail className="h-6 w-6 text-sage-600" aria-hidden="true" />
                  </div>

                  <h2 className="text-xl font-bold text-ink mb-2">
                    {config.headline}
                  </h2>

                  <p className="text-ink-soft text-sm mb-6">
                    {config.body}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      ref={inputRef}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full rounded-lg border border-line-strong px-4 py-2.5 text-sm text-ink placeholder:text-ink-muted focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20"
                    />

                    {error && (
                      <p className="text-sm text-red-600">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded-lg bg-sage-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2 disabled:opacity-50 transition-colors"
                    >
                      {isSubmitting ? 'Subscribing...' : config.button_text}
                    </button>
                  </form>

                  <button
                    onClick={handleDismiss}
                    className="mt-3 text-sm text-ink-muted hover:text-ink-soft transition-colors"
                  >
                    No thanks
                  </button>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sage-100">
                    <CheckCircle className="h-6 w-6 text-sage-600" aria-hidden="true" />
                  </div>

                  <h2 className="text-xl font-bold text-ink mb-2">
                    You're in!
                  </h2>

                  <p className="text-ink-soft text-sm mb-4">
                    {config.success_message}
                  </p>

                  {config.show_incentive && config.incentive_code && (
                    <div className="rounded-lg bg-sage-50 border border-sage-200 p-4 mb-4">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <Tag className="h-4 w-4 text-sage-600" aria-hidden="true" />
                        <span className="text-sm font-medium text-sage-800">
                          {config.incentive_description}
                        </span>
                      </div>
                      <p className="text-lg font-bold text-sage-900 font-mono tracking-wider">
                        {config.incentive_code}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleDismiss}
                    className="rounded-lg bg-sage-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2 transition-colors"
                  >
                    Start Shopping
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
